import { AppWindow } from '../../../windows/types';
import {
  CellPosition,
  SelectionCell,
  Area,
  Freeze,
  SheetView,
  Sheet,
  SheetSelection,
  IRange,
  Viewport,
} from '../../types';
import { convertPositionToArea } from '../area/area';
import { unionAreaWithMerges } from '../views/merges';
import {
  getCellPositionFromMousePosition,
  getColPositionFromMousePosition,
  getRowPositionFromMousePosition,
} from '../views/moveSelection';

import {
  getHorizontalBounds,
  getPanesBoundaries,
  getVerticalBounds,
} from '../views/views';

export const setActiveCell = (
  position: CellPosition,
  colViewport: Viewport,
  rowViewport: Viewport,
  cellType: SelectionCell,
  sheet: Sheet
  // freezedCols: Freeze,
  // freezedRows: Freeze,
  // merges: Area[]
) => {
  const { freezedCols, freezedRows } = sheet;

  let activeCell = { ...position };
  let activeCellArea: Area | undefined;

  let vrs = rowViewport.nonFrozenIndexes[0];
  if (freezedRows.frozen) {
    if (rowViewport.frozenIndexes[0] !== undefined)
      vrs = rowViewport.frozenIndexes[0];
  }

  let vcs = colViewport.nonFrozenIndexes[0];
  if (freezedCols.frozen) {
    if (colViewport.frozenIndexes[0] !== undefined)
      vcs = colViewport.frozenIndexes[0];
  }

  switch (cellType) {
    case SelectionCell.Column:
      activeCell = {
        ...activeCell,
        ri: vrs,
      };
      break;
    case SelectionCell.Row:
      activeCell = {
        ...activeCell,
        ci: vcs,
      };
      break;
    case SelectionCell.Root:
      activeCell = { ci: vcs, ri: vrs };
      break;
    // case SelectionCell.Cell:
    //   activeCellArea = unionAreaWithMerges(
    //     sheet.merges,
    //     convertPositionToArea(activeCell),
    //     sheet.maxColIndex,
    //     sheet.maxRowIndex
    //   );
  }

  // This will merge area on Row | Col selection as well
  activeCellArea = unionAreaWithMerges(
    sheet.merges,
    convertPositionToArea(activeCell)
  );

  return {
    activeCell,
    activeCellArea: activeCellArea ?? convertPositionToArea(activeCell),
  };
};

export const expandSelectionArea = (
  activeCell: CellPosition,
  newPos: CellPosition,
  // maxRowIndex: number,
  // maxColIndex: number,
  cellType: SelectionCell,
  sheet: Sheet
  // merges: Area[]
): Area => {
  const { maxColIndex, maxRowIndex } = sheet;

  const start: CellPosition = { ci: 0, ri: 0 };
  const end: CellPosition = { ci: maxColIndex, ri: maxRowIndex };

  switch (cellType) {
    case SelectionCell.Cell: {
      let newArea = expandSelectionToPosition(newPos, activeCell);
      return unionAreaWithMerges(sheet.merges, newArea);
    }
    case SelectionCell.Column:
      start.ri = 0;
      end.ci = activeCell.ci;
      return expandSelectionToPosition({ ...newPos, ri: 0 }, end);
    case SelectionCell.Row:
      start.ci = 0;
      end.ri = activeCell.ri;
      return expandSelectionToPosition({ ...newPos, ci: 0 }, end);
    case SelectionCell.Root:
      return expandSelectionToPosition(start, end);
    default:
      return convertPositionToArea(activeCell);
  }
};

export const expandSelectionToPosition = (
  newPos: CellPosition,
  activePos: CellPosition
): Area => ({
  start: {
    ri: Math.min(newPos.ri, activePos.ri),
    ci: Math.min(newPos.ci, activePos.ci),
  },
  end: {
    ri: Math.max(newPos.ri, activePos.ri),
    ci: Math.max(newPos.ci, activePos.ci),
  },
});

export const expandAreaToPartialChangedPosition = (
  changedPos: Partial<CellPosition>,
  range: IRange | undefined,
  activePos: CellPosition,
  area: Area
) => {
  if (range) {
    if (changedPos.ci !== undefined) {
      area.start.ci = range.start;
      area.end.ci = range.end;
    }
    if (changedPos.ri !== undefined) {
      area.start.ri = range.start;
      area.end.ri = range.end;
    }
  } else {
    if (changedPos.ci !== undefined) {
      area.start.ci = Math.min(changedPos.ci, activePos.ci);
      area.end.ci = Math.max(changedPos.ci, activePos.ci);
    }
    if (changedPos.ri !== undefined) {
      area.start.ri = Math.min(changedPos.ri, activePos.ri);
      area.end.ri = Math.max(changedPos.ri, activePos.ri);
    }
  }

  return area;
};

export const getColPositionFromClientPos = (
  windowX: number,
  windowWidth: number,
  sheet: Sheet,
  colViewport: Viewport,
  rowViewport: Viewport,
  // viewport: Viewport,
  freezedColsSum: number,
  isRtl: boolean,
  clientX: number,
  largerThanCenter: boolean
) => {
  const { freezeMinX, freezeMaxX, paneMinX, paneMaxX, unFreezedCi } =
    getHorizontalBounds(
      windowX,
      windowWidth,
      colViewport,
      rowViewport,
      sheet,
      freezedColsSum,
      isRtl
    );

  return getColPositionFromMousePosition(
    sheet,
    clientX,
    freezeMinX,
    freezeMaxX,
    paneMinX,
    paneMaxX,
    colViewport,
    isRtl,
    unFreezedCi,
    undefined,
    largerThanCenter
  );
};

export const getRowPositionFromClientPos = (
  windowY: number,
  windowHeight: number,
  sheet: Sheet,
  rowViewport: Viewport,
  freezedRowsSum: number,
  clientY: number,
  largerThanCenter: boolean
) => {
  const { freezeMinY, freezeMaxY, paneMinY, paneMaxY, unFreezedRi } =
    getVerticalBounds(
      windowY,
      windowHeight,
      rowViewport,
      sheet,
      freezedRowsSum
    );

  return getRowPositionFromMousePosition(
    sheet,
    clientY,
    freezeMinY,
    freezeMaxY,
    paneMinY,
    paneMaxY,
    rowViewport,
    unFreezedRi,
    undefined,
    largerThanCenter
  );
};

export const getCellPositionFromClientPosition = (
  window: AppWindow,
  sheet: Sheet,
  sheetView: SheetView,
  freezedColsSum: number,
  freezedRowsSum: number,
  clientX: number,
  clientY: number,
  isRtl: boolean,
  selection?: SheetSelection
) => {
  const { paneBoundaries, freezeBoundaries, unFreezedCi, unFreezedRi } =
    getPanesBoundaries(
      window.x,
      window.y,
      window.width,
      window.height,
      sheetView.colViewport,
      sheetView.rowViewport,
      freezedColsSum,
      freezedRowsSum,
      sheet,
      isRtl,
      sheetView.filterResults
    );

  return getCellPositionFromMousePosition(
    sheet,
    clientX,
    clientY,
    freezeBoundaries,
    paneBoundaries,
    sheetView.colViewport,
    sheetView.rowViewport,
    isRtl,
    unFreezedCi,
    unFreezedRi,
    selection?.area,
    sheetView.filterResults
  );
};
